import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ADMIN_COOKIE_KEY, useAdmin } from '../../hooks/useAdmin';
import Cookies from 'js-cookie';

const API_URL = 'https://guide.zerdegroup.kz/api/';

interface Category {
  id: number;
  title: string;
}

interface Subcategory {
  id: number;
  title: string;
  category_id: number;
  display_order: number;
}

const Subcategories: React.FC = () => {
  const { userRole } = useAdmin();
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    category_id: '',
  });

  const fetchCategories = async () => {
    try {
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      const response = await fetch(`${API_URL}/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при загрузке категорий');
      }

      const data = await response.json();
      setCategories(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    }
  };

  const fetchSubcategories = async () => {
    try {
      setLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      
      const response = await fetch(`${API_URL}/subcategories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при загрузке подкатегорий');
      }

      const data = await response.json();
      setSubcategories(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, []);

  const handleOpenDialog = (subcategory?: Subcategory) => {
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setFormData({
        title: subcategory.title,
        category_id: String(subcategory.category_id),
      });
    } else {
      setEditingSubcategory(null);
      setFormData({
        title: '',
        category_id: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSubcategory(null);
    setFormData({ title: '', category_id: '' });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);

      const url = editingSubcategory 
        ? `${API_URL}/subcategories/${editingSubcategory.id}`
        : `${API_URL}/subcategories`;

      const method = editingSubcategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          category_id: Number(formData.category_id),
        }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка при ${editingSubcategory ? 'обновлении' : 'создании'} подкатегории`);
      }

      handleCloseDialog();
      fetchSubcategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту подкатегорию?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);

      const response = await fetch(`${API_URL}/subcategories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при удалении подкатегории');
      }

      fetchSubcategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryTitle = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.title : 'Неизвестная категория';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Управление подкатегориями
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить подкатегорию
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : subcategories.length > 0 ? (
              subcategories.map((subcategory) => (
                <TableRow key={subcategory.id}>
                  <TableCell>{subcategory.id}</TableCell>
                  <TableCell>{subcategory.title}</TableCell>
                  <TableCell>{getCategoryTitle(subcategory.category_id)}</TableCell>
                  <TableCell>{subcategory.display_order}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(subcategory)}
                    >
                      <EditIcon />
                    </IconButton>
                    {userRole === 'admin' && (
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(subcategory.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubcategory ? 'Редактировать подкатегорию' : 'Создать подкатегорию'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Название"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Категория</InputLabel>
              <Select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                label="Категория"
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.title || !formData.category_id || loading}
          >
            {loading ? <CircularProgress size={24} /> : editingSubcategory ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subcategories; 